import React from "react";
import styles from "./FeaturesPage.module.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { normalizeLanguageCode } from "../supportedLanguages";
import globalFeatureContent from "../globalFeatureContent.json";
import {
  getMicrosoftStoreUrl,
  trackMicrosoftStoreClick,
} from "../utils/microsoftStore";
import ComparisonTable from "./ComparisonTable";

type FeatureItem = {
  id: string;
  title: string;
  subtitle: string;
  bullets: string[];
  embedUrl: string; // ✅ Cloudflare Stream iframe URL
  reversed?: boolean;
};

type GlobalFeatureContent = (typeof globalFeatureContent)["en"];

const globalFeatureContentByLanguage = globalFeatureContent as Record<
  string,
  GlobalFeatureContent
>;

const createCloudflareEmbedUrl = (videoId: string) => {
  const videoBaseUrl =
    `https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/${videoId}`;
  const posterUrl = `${videoBaseUrl}/thumbnails/thumbnail.jpg?height=600`;

  return `${videoBaseUrl}/iframe?muted=true&loop=true&autoplay=true&poster=${encodeURIComponent(
    posterUrl,
  )}&controls=false`;
};

const FeaturesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguageCode(i18n.language);
  const localizedGlobalFeatures =
    globalFeatureContentByLanguage[language] || globalFeatureContentByLanguage.en;

  const features: FeatureItem[] = [
    {
      id: "templates",
      title: t("features_page.templates.title"),
      subtitle: t("features_page.templates.subtitle"),
      bullets: [
        t("features_page.templates.bullet1"),
        t("features_page.templates.bullet2"),
      ],
      // ✅ Cloudflare에서 복사한 iframe src 값
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/c68a791f2a130edfca63f86caa0782c9/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2Fc68a791f2a130edfca63f86caa0782c9%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
    },
    {
      id: "thumbnail",
      title: t("features_page.thumbnail.title"),
      subtitle: t("features_page.thumbnail.subtitle"),
      bullets: [
        t("features_page.thumbnail.bullet1"),
        t("features_page.thumbnail.bullet2"),
      ],
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/781a79b3118c455a29fb48f9fb461939/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2F781a79b3118c455a29fb48f9fb461939%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
      reversed: true,
    },
    {
      id: "upload",
      title: t("features_page.upload.title"),
      subtitle: t("features_page.upload.subtitle"),
      bullets: [
        t("features_page.upload.bullet1"),
        t("features_page.upload.bullet2"),
      ],
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/540c6cbca12fc03b3c8e8494cdda28bf/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2F540c6cbca12fc03b3c8e8494cdda28bf%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
    },
    {
      id: "ai_models",
      title: t("features_page.ai_models.title"),
      subtitle: t("features_page.ai_models.subtitle"),
      bullets: [
        t("features_page.ai_models.bullet1"),
        t("features_page.ai_models.bullet2"),
      ],
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/6d5e0fe9600ed6d8c9ae30ee88f6ee60/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2F6d5e0fe9600ed6d8c9ae30ee88f6ee60%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
      reversed: true,
    },
    {
      id: "sync",
      title: t("features_page.sync.title"),
      subtitle: t("features_page.sync.subtitle"),
      bullets: [
        t("features_page.sync.bullet1"),
        t("features_page.sync.bullet2"),
      ],
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/b2695546a40859b45b97444bc5d76c0c/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2Fb2695546a40859b45b97444bc5d76c0c%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
    },
    {
      id: "script",
      title: t("features_page.script.title"),
      subtitle: t("features_page.script.subtitle"),
      bullets: [
        t("features_page.script.bullet1"),
        t("features_page.script.bullet2"),
      ],
      embedUrl:
        "https://customer-unwg1qdz6bidl9iw.cloudflarestream.com/6267956768e57324fc723e0a907aa337/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-unwg1qdz6bidl9iw.cloudflarestream.com%2F6267956768e57324fc723e0a907aa337%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false",
      reversed: true,
    },
    {
      id: "guided-workflow",
      title: localizedGlobalFeatures.guidedWorkflowTitle,
      subtitle: localizedGlobalFeatures.guidedWorkflowText,
      bullets: [
        localizedGlobalFeatures.guidedWorkflowPoint1,
        localizedGlobalFeatures.guidedWorkflowPoint2,
        localizedGlobalFeatures.guidedWorkflowPoint3,
      ],
      embedUrl: createCloudflareEmbedUrl(
        "ac0954243400924a364eafb42aa6f22c",
      ),
    },
    {
      id: "subtitle-effects",
      title: localizedGlobalFeatures.subtitleEffectsTitle,
      subtitle: localizedGlobalFeatures.subtitleEffectsText,
      bullets: [],
      embedUrl: createCloudflareEmbedUrl(
        "40847416e879a45d7550df449f934593",
      ),
      reversed: true,
    },
    {
      id: "image-motion",
      title: t("how_to_use.steps.image_motion.title"),
      subtitle: t("how_to_use.steps.image_motion.body"),
      bullets: [],
      embedUrl: createCloudflareEmbedUrl(
        "b065783fc2a6d72b0c39a5af9e8f44eb",
      ),
    },
    {
      id: "marketing-video",
      title: localizedGlobalFeatures.marketingTitle,
      subtitle: localizedGlobalFeatures.marketingText,
      bullets: [],
      embedUrl: createCloudflareEmbedUrl(
        "10726afee5f3590afc032d7419115a4e",
      ),
      reversed: true,
    },
  ];

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className={styles.page}>
      {/* HERO */}
      <section className={styles.hero}>
        <h1>{t("features_page.hero.title")}</h1>
        <p className={styles.heroSubtitle}>
          {t("features_page.hero.subtitle")}
        </p>

        <div className={styles.heroNav}>
          {features.map((f) => (
            <button
              key={f.id}
              type="button"
              className={styles.heroNavButton}
              onClick={() => scrollToSection(f.id)}
            >
              {f.title}
            </button>
          ))}
        </div>
      </section>

      {/* 기능 상세 섹션들 */}
      <section className={styles.featureList}>
        {features.map((f) => (
          <article
            key={f.id}
            id={f.id}
            className={`${styles.featureSection} ${
              f.reversed ? styles.featureSectionReversed : ""
            }`}
          >
            <div className={styles.featureText}>
              <h2>{f.title}</h2>
              <p className={styles.subtitle}>{f.subtitle}</p>
              {f.bullets.length > 0 && (
                <ul>
                  {f.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.featureMedia}>
              {/* ✅ Cloudflare Stream iframe 사용 */}
              <div className={styles.streamWrapper}>
                <iframe
                  src={f.embedUrl}
                  loading="lazy"
                  className={styles.streamIframe}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  title={f.title}
                />
              </div>
            </div>
          </article>
        ))}
      </section>

      <ComparisonTable language={language} />

      <section className={styles.globalAudienceSection}>
        <h2>{localizedGlobalFeatures.globalTitle}</h2>
        <p>{localizedGlobalFeatures.globalText}</p>
      </section>

      {/* 마지막 CTA */}
      <section className={styles.bottomCta}>
        <div>
          <h2>{t("features_page.bottom_cta.title")}</h2>
          <p>{t("features_page.bottom_cta.subtitle")}</p>
          <div className={styles.bottomCtaActions}>
            <a
              href={getMicrosoftStoreUrl("features_page")}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryCta}
              onClick={() => trackMicrosoftStoreClick("features_page")}
            >
              {t("home_page.final.primary_cta")}
            </a>
            <Link
              to={`/${language}/sample-videos`}
              className={styles.secondaryCta}
            >
              {t("home_page.final.secondary_cta1")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
